import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  DatePicker,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Tag,
  Typography,
  message,
} from 'antd';
import {
  DownloadOutlined,
  FileTextOutlined,
  PrinterOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useQuery } from 'react-query';
import dayjs from 'dayjs';
import api from '../services/api';

const { Text } = Typography;
const { RangePicker } = DatePicker;

// Same brand asset the PDF endpoint embeds, so print and PDF look alike.
const LOGO_URL = `${process.env.REACT_APP_API_URL || 'http://localhost:8000/api'}/images/currentcare-logo.png`;

const DEFAULT_FISCAL_YEAR = '2082-2083';

const money = (value) =>
  Number(value || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const emptyFigures = {
  opening_balance: 0,
  taxable_sales: 0,
  sales_return: 0,
  taxable_purchase: 0,
  purchase_return: 0,
  closing_balance: 0,
};

const ConfirmationLetter = () => {
  const [form] = Form.useForm();
  const [customerId, setCustomerId] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [summary, setSummary] = useState(null);
  const [figures, setFigures] = useState(emptyFigures);
  const [vatRate, setVatRate] = useState(13);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const { data: savedCompany } = useQuery('company-profile', async () => {
    const res = await api.get('/admin/company-profile');
    return res.data.data;
  });

  const period = Form.useWatch('period', form);
  const fiscalYear = Form.useWatch('fiscal_year', form);
  const customerName = Form.useWatch('customer_name', form);
  const customerVat = Form.useWatch('customer_vat', form);
  const customerAddress = Form.useWatch('customer_address', form);
  const customerPhone = Form.useWatch('customer_phone', form);
  const contactName = Form.useWatch('contact_name', form);
  const contactPhone = Form.useWatch('contact_phone', form);

  const { data: customers = [], isFetching: searching } = useQuery(
    ['letter-customers', customerSearch],
    async () => {
      const params = new URLSearchParams();
      params.append('user_type', 'customer');
      params.append('per_page', '30');
      if (customerSearch) params.append('search', customerSearch);
      const res = await api.get(`/admin/users?${params.toString()}`);
      const list = res.data?.data || res.data || [];
      return Array.isArray(list) ? list : list.data || [];
    },
    { keepPreviousData: true },
  );

  // A customer carries their own VAT number; keep it in the form so the
  // letter can be corrected without touching the customer record.
  const onPickCustomer = (value) => {
    setCustomerId(value);
    const picked = customers.find((c) => c.id === value);
    if (picked) {
      form.setFieldsValue({
        customer_name: picked.name || picked.full_name,
        customer_vat: picked.vat_number || '',
        customer_address: picked.address === 'Address not set' ? '' : picked.address || '',
        customer_phone: picked.phone || '',
      });
    }
    setSummary(null);
    setFigures(emptyFigures);
  };

  const loadSummary = async () => {
    if (!customerId) {
      message.warning('Select a customer first');
      return;
    }
    const values = form.getFieldsValue();
    if (!values.period || !values.period[0] || !values.period[1]) {
      message.warning('Select the period covered by the letter');
      return;
    }

    setLoadingSummary(true);
    try {
      const res = await api.get('/admin/confirmation-letter/summary', {
        params: {
          customer_id: customerId,
          from: values.period[0].format('YYYY-MM-DD'),
          to: values.period[1].format('YYYY-MM-DD'),
        },
      });
      const data = res.data.data;
      setSummary(data);
      setVatRate(data.vat_rate);
      form.setFieldsValue({
        customer_name: data.customer.name,
        customer_vat: data.customer.vat_number || '',
        customer_address: data.customer.address || form.getFieldValue('customer_address') || '',
        customer_phone: data.customer.phone || form.getFieldValue('customer_phone') || '',
      });
      setFigures({
        opening_balance: data.figures.opening_balance,
        taxable_sales: data.figures.taxable_sales,
        sales_return: data.figures.sales_return,
        taxable_purchase: data.figures.taxable_purchase,
        purchase_return: data.figures.purchase_return,
        closing_balance: data.figures.closing_balance,
      });
      message.success('Loaded the recorded figures. Adjust them if your books differ.');
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to load customer figures');
    } finally {
      setLoadingSummary(false);
    }
  };

  const restoreSystemFigures = () => {
    if (!summary) return;
    setFigures({
      opening_balance: summary.figures.opening_balance,
      taxable_sales: summary.figures.taxable_sales,
      sales_return: summary.figures.sales_return,
      taxable_purchase: summary.figures.taxable_purchase,
      purchase_return: summary.figures.purchase_return,
      closing_balance: summary.figures.closing_balance,
    });
    setVatRate(summary.vat_rate);
  };

  // Derived rows mirror the table printed on the letter.
  const rows = useMemo(() => {
    const net = (a, b) => Number((Number(a || 0) - Number(b || 0)).toFixed(2));
    const vat = (amount) => Number(((Number(amount || 0) * Number(vatRate || 0)) / 100).toFixed(2));
    const totalSales = net(figures.taxable_sales, figures.sales_return);
    const totalPurchase = net(figures.taxable_purchase, figures.purchase_return);
    return {
      taxableSales: Number(figures.taxable_sales || 0),
      salesReturn: Number(figures.sales_return || 0),
      totalSales,
      totalSalesVat: vat(totalSales),
      taxablePurchase: Number(figures.taxable_purchase || 0),
      purchaseReturn: Number(figures.purchase_return || 0),
      totalPurchase,
      totalPurchaseVat: vat(totalPurchase),
    };
  }, [figures, vatRate]);

  const company = summary?.company;

  const payload = () => ({
    customer: {
      name: form.getFieldValue('customer_name') || customerName,
      vat_number: form.getFieldValue('customer_vat') || customerVat || '',
      address: form.getFieldValue('customer_address') || customerAddress || '',
      phone: form.getFieldValue('customer_phone') || customerPhone || '',
    },
    contact: {
      name: form.getFieldValue('contact_name') || contactName || '',
      phone: form.getFieldValue('contact_phone') || contactPhone || '',
    },
    fiscal_year: form.getFieldValue('fiscal_year') || DEFAULT_FISCAL_YEAR,
    period: {
      from: form.getFieldValue('period')?.[0]?.format('YYYY-MM-DD'),
      to: form.getFieldValue('period')?.[1]?.format('YYYY-MM-DD'),
    },
    vat_rate: Number(vatRate || 0),
    figures: {
      opening_balance: Number(figures.opening_balance || 0),
      taxable_sales: Number(figures.taxable_sales || 0),
      sales_return: Number(figures.sales_return || 0),
      taxable_purchase: Number(figures.taxable_purchase || 0),
      purchase_return: Number(figures.purchase_return || 0),
      closing_balance: Number(figures.closing_balance || 0),
    },
  });

  const downloadPdf = async () => {
    const body = payload();
    if (!body.customer.name || !body.period.from || !body.period.to) {
      message.warning('Fill in the customer, fiscal year and period first');
      return;
    }
    setDownloading(true);
    try {
      const res = await api.post('/admin/confirmation-letter/pdf', body, {
        responseType: 'blob',
      });
      const disposition = res.headers['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";]+)"?/);
      const name = match ? match[1] : 'confirmation-letter.pdf';

      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = name;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(blobUrl);
      message.success('Letter downloaded');
    } catch (error) {
      message.error('Failed to generate the letter PDF');
    } finally {
      setDownloading(false);
    }
  };

  useEffect(() => {
    form.setFieldsValue({
      fiscal_year: DEFAULT_FISCAL_YEAR,
      period: [dayjs().startOf('year'), dayjs().endOf('year')],
    });
  }, [form]);

  // The foot of the letter starts from the contact saved on the company
  // profile and can be changed for a single letter.
  useEffect(() => {
    if (savedCompany) {
      form.setFieldsValue({
        contact_name: savedCompany.contact_person_name || '',
        contact_phone: savedCompany.contact_person_phone || '',
      });
    }
  }, [savedCompany, form]);

  const periodLabel =
    period && period[0] && period[1]
      ? `${period[0].format('DD MMM YYYY')} – ${period[1].format('DD MMM YYYY')}`
      : '';

  return (
    <div className="page-container">
      <div className="letter-no-print">
        <Row gutter={[16, 16]}>
          <Col xs={24} lg={10}>
            <Card title="Letter details" className="letter-form-card">
              <Form form={form} layout="vertical">
                <Form.Item label="Customer" required>
                  <Select
                    showSearch
                    allowClear
                    value={customerId}
                    onChange={onPickCustomer}
                    onSearch={setCustomerSearch}
                    loading={searching}
                    placeholder="Search a customer"
                    suffixIcon={<SearchOutlined />}
                    filterOption={false}
                    notFoundContent={searching ? 'Searching…' : 'No customer found'}
                    options={(customers || []).map((c) => ({
                      value: c.id,
                      label: c.name || c.full_name || `User ${c.id}`,
                    }))}
                  />
                </Form.Item>

                <Form.Item label="Customer address" name="customer_address">
                  <Input placeholder="Printed under the customer's name in To:" />
                </Form.Item>

                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item label="Customer VAT No." name="customer_vat">
                      <Input placeholder="125522908" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Customer phone" name="customer_phone">
                      <Input placeholder="98XXXXXXXX" />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={12}>
                  <Col span={12}>
                    <Form.Item
                      label="Fiscal Year"
                      name="fiscal_year"
                      tooltip="Printed on the letter, e.g. 2082-2083"
                    >
                      <Input placeholder={DEFAULT_FISCAL_YEAR} />
                    </Form.Item>
                  </Col>
                  <Col span={12} />
                </Row>

                <Form.Item label="Period covered" name="period" required>
                  <RangePicker style={{ width: '100%' }} format="DD MMM YYYY" />
                </Form.Item>

                <Divider style={{ margin: '4px 0 12px' }} />
                <Text type="secondary">For further contact us (foot of the letter)</Text>
                <Row gutter={12} style={{ marginTop: 8 }}>
                  <Col span={12}>
                    <Form.Item label="Contact person" name="contact_name">
                      <Input placeholder="Defaults to the company profile" />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="Contact phone" name="contact_phone">
                      <Input placeholder="98XXXXXXXX" />
                    </Form.Item>
                  </Col>
                </Row>

                <Button
                  type="primary"
                  icon={<ReloadOutlined />}
                  onClick={loadSummary}
                  loading={loadingSummary}
                  block
                >
                  Load figures from system
                </Button>
              </Form>

              {summary && (
                <>
                  <Divider style={{ margin: '16px 0 12px' }} />
                  <Text type="secondary">Recorded in the system for this period</Text>
                  <Descriptions column={1} size="small" style={{ marginTop: 8 }}>
                    <Descriptions.Item label="Online orders">
                      {summary.counts.orders} · NPR {money(summary.channel_split.online_taxable)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Counter sales">
                      {summary.counts.offline_sales} · NPR{' '}
                      {money(summary.channel_split.offline_taxable)}
                    </Descriptions.Item>
                    <Descriptions.Item label="Amount received">
                      NPR {money(summary.figures.amount_received)}
                    </Descriptions.Item>
                  </Descriptions>
                </>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={14}>
            <Card
              title="Figures"
              className="letter-form-card"
              extra={
                summary && (
                  <Button size="small" onClick={restoreSystemFigures}>
                    Reset to system
                  </Button>
                )
              }
            >
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="These figures are yours to correct"
                description="They are prefilled from the system, but the shop also trades offline, so adjust any amount to match your books before printing."
              />

              <Row gutter={12}>
                <Col span={8}>
                  <Form.Item label="Opening Balance">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step={0.01}
                      value={figures.opening_balance}
                      onChange={(v) => setFigures({ ...figures, opening_balance: v ?? 0 })}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Taxable Sales">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step={0.01}
                      value={figures.taxable_sales}
                      onChange={(v) => setFigures({ ...figures, taxable_sales: v ?? 0 })}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Sales Return">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step={0.01}
                      value={figures.sales_return}
                      onChange={(v) => setFigures({ ...figures, sales_return: v ?? 0 })}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={12}>
                <Col span={8}>
                  <Form.Item label="Taxable Purchase">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step={0.01}
                      value={figures.taxable_purchase}
                      onChange={(v) => setFigures({ ...figures, taxable_purchase: v ?? 0 })}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Purchase Return">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step={0.01}
                      value={figures.purchase_return}
                      onChange={(v) => setFigures({ ...figures, purchase_return: v ?? 0 })}
                    />
                  </Form.Item>
                </Col>
                <Col span={8}>
                  <Form.Item label="Closing Balance">
                    <InputNumber
                      style={{ width: '100%' }}
                      min={0}
                      step={0.01}
                      value={figures.closing_balance}
                      onChange={(v) => setFigures({ ...figures, closing_balance: v ?? 0 })}
                    />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item label="VAT rate (%)" style={{ maxWidth: 180 }}>
                <InputNumber
                  style={{ width: '100%' }}
                  min={0}
                  max={100}
                  step={0.01}
                  value={vatRate}
                  onChange={(v) => setVatRate(v ?? 0)}
                />
              </Form.Item>

              <Divider style={{ margin: '8px 0 12px' }} />
              <Space wrap>
                <Statistic
                  title="Total Sales (incl. VAT)"
                  value={rows.totalSales + rows.totalSalesVat}
                  precision={2}
                  prefix="NPR "
                  valueStyle={{ fontSize: 18 }}
                />
                <Button type="primary" icon={<PrinterOutlined />} onClick={() => window.print()}>
                  Print letter
                </Button>
                <Button icon={<DownloadOutlined />} loading={downloading} onClick={downloadPdf}>
                  Download PDF
                </Button>
              </Space>
            </Card>
          </Col>
        </Row>
      </div>

      <Card
        className="letter-preview-card"
        title={
          <span>
            <FileTextOutlined /> Preview
          </span>
        }
        extra={<Tag color="blue">A4</Tag>}
      >
        <Spin spinning={loadingSummary}>
          <div className="letter-sheet">
            <div className="letter-head">
              <img
                className="letter-logo"
                src={LOGO_URL}
                alt="Company logo"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                }}
              />
              <div className="letter-company">{company?.legal_name || 'Current Care Nepal'}</div>
              {company?.address && <div className="letter-meta">{company.address}</div>}
              <div className="letter-meta">
                {[
                  company?.pan_number ? `PAN: ${company.pan_number}` : null,
                  company?.vat_number ? `VAT No.: ${company.vat_number}` : null,
                  company?.phone,
                  company?.email,
                ]
                  .filter(Boolean)
                  .join('   |   ')}
              </div>
            </div>

            <div className="letter-party">
              <div>
                <strong>To:</strong> {customerName || '—'}
              </div>
              {customerAddress ? <div className="letter-party-line">{customerAddress}</div> : null}
              {customerVat ? <div className="letter-party-line">Vat No.: {customerVat}</div> : null}
              {customerPhone ? (
                <div className="letter-party-line">Phone: {customerPhone}</div>
              ) : null}
            </div>

            <div className="letter-subject">
              Confirmation of Business Transaction for F/Y: {fiscalYear || DEFAULT_FISCAL_YEAR}
            </div>

            <p>Dear Sir / Madam,</p>
            <p className="letter-justify">
              With reference to the above subject, we are sending you the details of transaction
              &amp; balance for the fiscal year{' '}
              {String(fiscalYear || DEFAULT_FISCAL_YEAR).replace('/', '-')}.
            </p>

            <div className="letter-section">Transaction Details:</div>

            <table className="letter-table">
              <thead>
                <tr>
                  <th>Particular</th>
                  <th className="right">Amount</th>
                  <th className="right">{Number(vatRate || 0)}% Vat</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Opening Balance</td>
                  <td className="right">{money(figures.opening_balance)}</td>
                  <td className="right" />
                </tr>
                <tr>
                  <td>Taxable Sales</td>
                  <td className="right">{money(rows.taxableSales)}</td>
                  <td className="right">
                    {money((Number(figures.taxable_sales || 0) * Number(vatRate || 0)) / 100)}
                  </td>
                </tr>
                <tr>
                  <td>Sales Return</td>
                  <td className="right">{money(rows.salesReturn)}</td>
                  <td className="right">
                    {money((Number(figures.sales_return || 0) * Number(vatRate || 0)) / 100)}
                  </td>
                </tr>
                <tr className="letter-total">
                  <td>Total Sales</td>
                  <td className="right">{money(rows.totalSales)}</td>
                  <td className="right">{money(rows.totalSalesVat)}</td>
                </tr>
                <tr>
                  <td className="letter-annex">ANNEX-13 Entry Amount</td>
                  <td colSpan={2} />
                </tr>
                <tr>
                  <td>Taxable Purchase</td>
                  <td className="right">{money(rows.taxablePurchase)}</td>
                  <td className="right">
                    {money((Number(figures.taxable_purchase || 0) * Number(vatRate || 0)) / 100)}
                  </td>
                </tr>
                <tr>
                  <td>Purchase Return</td>
                  <td className="right">{money(rows.purchaseReturn)}</td>
                  <td className="right">
                    {money((Number(figures.purchase_return || 0) * Number(vatRate || 0)) / 100)}
                  </td>
                </tr>
                <tr className="letter-total">
                  <td>Total Purchase</td>
                  <td className="right">{money(rows.totalPurchase)}</td>
                  <td className="right">{money(rows.totalPurchaseVat)}</td>
                </tr>
                <tr>
                  <td className="letter-annex">ANNEX-13 Entry Amount</td>
                  <td colSpan={2} />
                </tr>
                <tr className="letter-total">
                  <td>Closing Balance</td>
                  <td className="right" colSpan={2}>
                    {money(figures.closing_balance)}
                  </td>
                </tr>
              </tbody>
            </table>

            <p className="letter-justify">
              This is for the confirmation of the total transaction &amp; balance in the Fiscal Year{' '}
              {String(fiscalYear || DEFAULT_FISCAL_YEAR).replace('/', '-')}.
            </p>
            <p className="letter-justify">
              Please, confirm the above transaction with yours &amp; kindly see same letter duly
              signed and stamped within 15 days, otherwise we shall take this balance as conformed
              &amp; accepted by you.
            </p>
            <p className="letter-justify">
              We had a good business together &amp; we hope to do so again in the coming days.
            </p>
            {(contactName || contactPhone) && (
              <p>
                For further contact us:
                {contactName}
                {contactPhone ? ` – ${contactPhone}` : ''}
              </p>
            )}

            <div className="letter-signatures">
              <div>
                <div className="letter-sign-line" />
                <div className="letter-sign-label">Authorized Signature &amp; Stamp</div>
                <div className="letter-sign-name">
                  {company?.legal_name || 'Current Care Nepal'}
                </div>
              </div>
              <div>
                <div className="letter-sign-line" />
                <div className="letter-sign-label">Authorized Signature &amp; Stamp</div>
                <div className="letter-sign-name">{customerName || '—'}</div>
              </div>
            </div>

            <div className="letter-footer">
              {company?.legal_name || 'Current Care Nepal'}
              {company?.address ? `   |   ${company.address}` : ''}
              {periodLabel ? (
                <>
                  <br />
                  This letter reflects the transactions recorded between {periodLabel}.
                </>
              ) : null}
            </div>
          </div>
        </Spin>
      </Card>
    </div>
  );
};

export default ConfirmationLetter;
